import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add,
  Delete,
  MoreVert,
  Description,
  Visibility,
  Assessment,
  Business,
  AccountBalance,
  Receipt,
  FileDownload
} from '@mui/icons-material';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { statutoryReportsAPI } from '../../services/api';
import { downloadExcelFile, downloadPDFFile } from '../../utils/fileDownload';

const validationSchema = Yup.object({
  reportType: Yup.string().required('Report type is required'),
  state: Yup.string().required('State is required'),
  month: Yup.number()
    .min(1, 'Month must be between 1 and 12')
    .max(12, 'Month must be between 1 and 12')
    .required('Month is required'),
  year: Yup.number()
    .min(2020, 'Year must be 2020 or later')
    .max(2030, 'Year must be 2030 or earlier')
    .required('Year is required'),
  description: Yup.string().required('Description is required')
});

const StatutoryReportsManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  // const [editingReport, setEditingReport] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [generatingReport, setGeneratingReport] = useState(false);

  const reportCategories = [
    {
      id: 'minimum-wages',
      name: 'Minimum Wages Act',
      icon: <AccountBalance />,
      reports: [
        { id: 'mw-form-xiv-a', name: 'Form XIV-A - Register of Wages', description: 'Rule 26(1)' },
        { id: 'mw-form-xi', name: 'Form XI - Wage Slip', description: 'Rule 26(2)' }
      ]
    },
    {
      id: 'bonus-act',
      name: 'Payment of Bonus Act',
      icon: <Receipt />,
      reports: [
        { id: 'bonus-form-a', name: 'Form A - Statement of Available Surplus', description: 'Annual return' },
        { id: 'bonus-form-b', name: 'Form B - Set-off and Set-on', description: 'Allocable surplus' },
        { id: 'bonus-form-d', name: 'Form D - Register of Bonus Payments', description: 'Bonus register' }
      ]
    },
    {
      id: 'equal-remuneration',
      name: 'Equal Remuneration Act',
      icon: <Business />,
      reports: [
        { id: 'er-form-d', name: 'Form D - Equal Remuneration Register', description: 'Gender pay equality' }
      ]
    },
    {
      id: 'shops-establishment',
      name: 'Shops & Commercial Establishment',
      icon: <Assessment />,
      reports: [
        { id: 'se-leave-register', name: 'Leave Register', description: 'All states' },
        { id: 'se-overtime-register', name: 'Overtime Register', description: 'All states' },
        { id: 'se-attendance-register', name: 'Attendance Register', description: 'All states' },
        { id: 'se-holiday-register', name: 'Holiday Register', description: 'All states' },
        { id: 'se-accident-register', name: 'Accident Register', description: 'All states' },
        { id: 'se-wage-register', name: 'Wage Register', description: 'All states' }
      ]
    },
    {
      id: 'income-tax',
      name: 'Income Tax & PAN',
      icon: <Description />,
      reports: [
        { id: 'it-form-240-monthly', name: 'Form 240 - Monthly TDS Statement', description: 'Monthly return' },
        { id: 'it-form-240-quarterly', name: 'Form 240 - Quarterly TDS Statement', description: 'Quarterly return' },
        { id: 'pan-verification', name: 'PAN Verification Register', description: 'PAN validation' }
      ]
    },
    {
      id: 'clc-returns',
      name: 'CLC Returns',
      icon: <Assessment />,
      reports: [
        { id: 'clc-consolidated', name: 'Consolidated Returns', description: 'Monthly/Quarterly' },
        { id: 'clc-annual', name: 'Annual Returns', description: 'Yearly return' }
      ]
    }
  ];

  const states = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
    'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu & Kashmir', 'Ladakh',
    'Chandigarh', 'Dadra & Nagar Haveli', 'Daman & Diu', 'Lakshadweep',
    'Puducherry', 'Andaman & Nicobar Islands'
  ];

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await statutoryReportsAPI.getAll({
        page: 1,
        limit: 50,
        sortBy: "createdAt",
        sortOrder: "desc",
      });
      const reportsData = response.data.data?.reports || [];
      console.log("=== STATUTORY REPORTS API DEBUG ===");
      console.log("Current logged-in user:", user);
      console.log("Full API response:", response.data);
      console.log("Reports data:", reportsData);
      if (reportsData.length > 0) {
        console.log("First report full object:", JSON.stringify(reportsData[0], null, 2));
        console.log("GeneratedBy field:", reportsData[0].generatedBy);
        console.log("User field:", reportsData[0].user);
        console.log("Metadata field:", reportsData[0].metadata);
        console.log("Metadata.generatedBy field:", reportsData[0].metadata?.generatedBy);
        console.log("All keys in first report:", Object.keys(reportsData[0]));
      }
      console.log("=== END STATUTORY DEBUG ===");
      setReports(reportsData);
    } catch (error) {
      console.error('Error loading statutory reports:', error);
      enqueueSnackbar(
        `Failed to load statutory reports: ${error.response?.data?.error || error.message}`,
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, user]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleGenerateReport = async (values) => {
    try {
      setGeneratingReport(true);
      
      console.log('Form values:', values);
      
      // Validate required fields
      if (!values.reportType) {
        enqueueSnackbar('Please select a report type', { variant: 'error' });
        return;
      }
      
      if (!values.state) {
        enqueueSnackbar('Please select a state', { variant: 'error' });
        return;
      }
      
      if (!values.month || !values.year) {
        enqueueSnackbar('Please select month and year', { variant: 'error' });
        return;
      }
      
      // Convert report type to backend format (uppercase with underscores)
      const reportTypeMap = {
        // Minimum Wages Act
        'mw-form-xiv-a': 'MW_FORM_XIV_A',
        'mw-form-xi': 'MW_FORM_XI',
        'mw-form-xiii': 'MW_FORM_XIII',
        'mw-form-xv': 'MW_FORM_XV',
        
        // Payment of Bonus Act
        'bonus-form-a': 'BONUS_FORM_A',
        'bonus-form-b': 'BONUS_FORM_B',
        'bonus-form-d': 'BONUS_FORM_D',
        
        // Equal Remuneration Act
        'er-form-d': 'ER_FORM_D',
        
        // Shops & Commercial Establishment
        'se-leave-register': 'SE_LEAVE_REGISTER',
        'se-overtime-register': 'SE_OVERTIME_REGISTER',
        'se-attendance-register': 'SE_ATTENDANCE_REGISTER',
        'se-holiday-register': 'SE_HOLIDAY_REGISTER',
        'se-accident-register': 'SE_ACCIDENT_REGISTER',
        'se-wage-register': 'SE_WAGE_REGISTER'
      };
      
      const backendReportType = reportTypeMap[values.reportType] || values.reportType.toUpperCase().replace(/-/g, '_');
      
      const payload = {
        period: {
          month: parseInt(values.month),
          year: parseInt(values.year)
        },
        state: values.state
      };
      
      console.log('Payload being sent:', payload);
      console.log('Original report type:', values.reportType);
      console.log('Backend report type:', backendReportType);
      
      const response = await statutoryReportsAPI.generate(backendReportType, payload);
      console.log('API response:', response);
      
      enqueueSnackbar('Report generated successfully', { variant: 'success' });
      setOpenDialog(false);
      loadReports(); // Reload reports to get the new one
    } catch (error) {
      console.error('Error generating report:', error);
      console.error('Error response:', error.response?.data);
      enqueueSnackbar(
        `Failed to generate report: ${error.response?.data?.error || error.message}`,
        { variant: 'error' }
      );
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleDownloadReport = async (reportId, format = 'excel') => {
    try {
      let response;
      if (format === 'excel') {
        response = await statutoryReportsAPI.downloadExcel(reportId);
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        downloadExcelFile(blob, reportId);
      } else if (format === 'pdf') {
        response = await statutoryReportsAPI.downloadPDF(reportId);
        const blob = new Blob([response.data], { type: "application/pdf" });
        downloadPDFFile(blob, reportId);
      }
      enqueueSnackbar(`${format.toUpperCase()} file downloaded successfully`, { variant: 'success' });
    } catch (error) {
      console.error('Error downloading report:', error);
      enqueueSnackbar(
        `Failed to download ${format.toUpperCase()} file: ${error.response?.data?.error || error.message}`,
        { variant: 'error' }
      );
    }
  };

  const handleDeleteReport = async (reportId) => {
    try {
      await statutoryReportsAPI.delete(reportId);
      enqueueSnackbar('Report deleted successfully', { variant: 'success' });
      loadReports(); // Reload reports to reflect the deletion
    } catch (error) {
      console.error('Error deleting report:', error);
      enqueueSnackbar(
        `Failed to delete report: ${error.response?.data?.error || error.message}`,
        { variant: 'error' }
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      reportType: '',
      state: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      description: ''
    },
    validationSchema,
    onSubmit: handleGenerateReport
  });

  const handleMenuOpen = (event, report) => {
    setAnchorEl(event.currentTarget);
    setSelectedReport(report);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReport(null);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      completed: { color: 'success', label: 'Completed' },
      pending: { color: 'warning', label: 'Pending' },
      generating: { color: 'info', label: 'Generating' },
      failed: { color: 'error', label: 'Failed' }
    };
    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  const getReportCategory = (reportType) => {
    for (const category of reportCategories) {
      const report = category.reports.find(r => r.id === reportType);
      if (report) return { category, report };
    }
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Statutory Reports Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              // reset selection and open dialog
              formik.resetForm();
              setOpenDialog(true);
            }}
          >
            Generate Report
          </Button>
        </Box>

        {/* Report Categories */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Available Report Categories
            </Typography>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {reportCategories.map((category, index) => (
                <Tab
                  key={category.id}
                  label={category.name}
                  icon={category.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
            
            <Box sx={{ mt: 2 }}>
              {reportCategories.map((category, index) => (
                <div
                  key={category.id}
                  role="tabpanel"
                  hidden={tabValue !== index}
                >
                  <Grid container spacing={2}>
                    {category.reports.map((report) => (
                      <Grid item xs={12} sm={6} md={4} key={report.id}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                              {report.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {report.description}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Add />}
                              onClick={() => {
                                formik.setValues({
                                  ...formik.values,
                                  reportType: report.id,
                                  description: report.description
                                });
                                setOpenDialog(true);
                              }}
                            >
                              Generate
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </div>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Reports Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Generated Reports
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Report</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Generated By</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report) => {
                    const categoryInfo = getReportCategory(report.reportType);
                    return (
                      <TableRow key={report._id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {report.reportName || report.reportType}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {categoryInfo?.category.name || 'Statutory Report'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{report.state || 'N/A'}</TableCell>
                        <TableCell>
                          {report.period?.month && report.period?.year
                            ? new Date(report.period.year, report.period.month - 1).toLocaleDateString('en-US', { 
                                month: 'long', 
                                year: 'numeric' 
                              })
                            : report.period?.startDate && report.period?.endDate
                            ? `${new Date(report.period.startDate).toLocaleDateString()} - ${new Date(report.period.endDate).toLocaleDateString()}`
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {report.metadata?.generatedBy?.firstName && report.metadata?.generatedBy?.lastName
                              ? `${report.metadata.generatedBy.firstName} ${report.metadata.generatedBy.lastName}`
                              : report.metadata?.generatedBy?.name
                              ? report.metadata.generatedBy.name
                              : report.metadata?.generatedBy?.email
                              ? report.metadata.generatedBy.email
                              : report.generatedBy?.firstName && report.generatedBy?.lastName
                              ? `${report.generatedBy.firstName} ${report.generatedBy.lastName}`
                              : report.generatedBy?.name
                              ? report.generatedBy.name
                              : report.generatedBy?.email
                              ? report.generatedBy.email
                              : report.user?.firstName && report.user?.lastName
                              ? `${report.user.firstName} ${report.user.lastName}`
                              : report.user?.name
                              ? report.user.name
                              : report.user?.email
                              ? report.user.email
                              : user?.firstName && user?.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user?.name
                              ? user.name
                              : user?.email
                              ? user.email
                              : "System"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {report.metadata?.generatedBy?.email || report.generatedBy?.email || report.user?.email || user?.email || ""}
                          </Typography>
                        </TableCell>
                        <TableCell>{report.fileUrls?.excel ? 'Available' : '-'}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, report)}
                            size="small"
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={reports.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </CardContent>
        </Card>

        {/* Generate Report Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Generate Statutory Report
          </DialogTitle>
          <form onSubmit={formik.handleSubmit}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Report Type</InputLabel>
                    <Select
                      name="reportType"
                      value={formik.values.reportType}
                      onChange={formik.handleChange}
                      label="Report Type"
                      error={formik.touched.reportType && Boolean(formik.errors.reportType)}
                    >
                      {reportCategories.map((category) => 
                        category.reports.map((report) => (
                          <MenuItem key={report.id} value={report.id}>
                            {report.name} - {category.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>State</InputLabel>
                    <Select
                      name="state"
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      label="State"
                      error={formik.touched.state && Boolean(formik.errors.state)}
                    >
                      {states.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Month</InputLabel>
                    <Select
                      name="month"
                      value={formik.values.month}
                      onChange={formik.handleChange}
                      label="Month"
                      error={formik.touched.month && Boolean(formik.errors.month)}
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <MenuItem key={i + 1} value={i + 1}>
                          {new Date(0, i).toLocaleDateString('en-US', { month: 'long' })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Year"
                    name="year"
                    type="number"
                    value={formik.values.year}
                    onChange={formik.handleChange}
                    error={formik.touched.year && Boolean(formik.errors.year)}
                    helperText={formik.touched.year && formik.errors.year}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    multiline
                    rows={3}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    error={formik.touched.description && Boolean(formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
              <Button 
                type="submit" 
                variant="contained"
                disabled={generatingReport}
              >
                {generatingReport ? 'Generating...' : 'Generate Report'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            handleDownloadReport(selectedReport._id, 'excel');
            handleMenuClose();
          }}>
            <FileDownload sx={{ mr: 1 }} />
            Download Excel
          </MenuItem>
          <MenuItem onClick={() => {
            handleDownloadReport(selectedReport._id, 'pdf');
            handleMenuClose();
          }}>
            <FileDownload sx={{ mr: 1 }} />
            Download PDF
          </MenuItem>
          <MenuItem onClick={() => {
            // View report details
            handleMenuClose();
          }}>
            <Visibility sx={{ mr: 1 }} />
            View Details
          </MenuItem>
          <MenuItem onClick={() => {
            handleDeleteReport(selectedReport._id);
            handleMenuClose();
          }}>
            <Delete sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Generating Report Overlay */}
        {generatingReport && (
          <Box sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            bgcolor: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 9999 
          }}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Generating Report...</Typography>
              <LinearProgress />
            </Card>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default StatutoryReportsManagement;